import { useEffect, useState } from "react";
import axios from "axios";
import { API_URL } from "../../constants";
import { SlotNames } from "./template-slot-values";

type Template = { id: string; name: string };
type Category = { id: string; title: string; parentCategory: string | null };

const NO_NAMES: SlotNames = { templateNameById: {}, categoryPathById: {} };

/** "HR › Business trips": the category's title behind the titles of its parents. */
function categoryPaths(categories: Category[]): Record<string, string> {
  const byId = new Map(categories.map((category) => [category.id, category]));
  const pathOf = (category: Category): string => {
    const parent = category.parentCategory && byId.get(category.parentCategory);
    return parent ? `${pathOf(parent)} › ${category.title}` : category.title;
  };
  return Object.fromEntries(
    categories.map((category) => [category.id, pathOf(category)])
  );
}

/**
 * The names of the templates and categories a company's document settings
 * point at (the settings hold only their ids). Until they load, or if they
 * cannot be loaded, the rows simply say less.
 */
export function useTemplateSlotNames(companyId: string | undefined): SlotNames {
  const [names, setNames] = useState(NO_NAMES);

  useEffect(() => {
    if (!companyId) return undefined;
    let cancelled = false;
    Promise.all([
      axios.get<Template[]>(
        `${API_URL}/${companyId}/files/templates/fetch-file-templates`
      ),
      axios.get<Category[]>(
        `${API_URL}/${companyId}/files/categories-fetch/customer`
      ),
    ])
      .then(([templates, categories]) => {
        if (cancelled) return;
        setNames({
          templateNameById: Object.fromEntries(
            templates.data.map((template) => [template.id, template.name])
          ),
          categoryPathById: categoryPaths(categories.data),
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  return names;
}
